<template>
  <div class="phone-input-wrapper">
    <v-phone-input
      class="phone-input"
      :model-value="modelValue"
      :country="activeCountry"
      :default-country="activeCountry || defaultCountry || undefined"
      :guess-country="guessCountryResolver"
      :label="placeholder"
      :disabled="disabled"
      invalid-message="Invalid phone number"
      density="compact"
      variant="outlined"
      color="primary"
      :error="showFieldError"
      :error-messages="showFieldError ? errorMessage : undefined"
      hide-details="auto"
      @update:model-value="onModelValue"
      @update:country="onCountryUpdate"
      @update:phone-object="onPhoneObject"
      @blur="touchedPhone = true"
    />

    <div
      v-if="hint && !showFieldError"
      class="v-messages theme--light mt-0.5 pl-3"
    >
      <div class="v-messages__wrapper">
        <div class="v-messages__message text-caption">
          {{ hint }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PhoneInput',
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Enter a phone number'
    },
    required: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: String,
      default: ''
    },
    country: {
      type: String,
      default: ''
    },
    defaultCountry: {
      type: String,
      default: ''
    },
    /** Parent-driven error outline (e.g. Register blur when internal @blur did not fire). */
    errorOutline: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      phoneCountry: this.defaultCountry || '',
      phoneValid: false,
      touchedPhone: false
    };
  },
  watch: {
    country: {
      immediate: true,
      handler(value) {
        if (value) this.phoneCountry = value;
      }
    },
    defaultCountry: {
      immediate: true,
      handler(value) {
        if (value && !this.country) this.phoneCountry = value;
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      const empty = !(this.modelValue && String(this.modelValue).trim());
      if (empty && !this.required) {
        this.phoneValid = true;
        this.$emit('validate', { valid: true, number: '' });
      }
    });
  },
  computed: {
    errorMessage() {
      if (this.required && !this.modelValue)
        return 'Phone number is required';

      return 'Invalid phone number';
    },
    showPhoneError() {
      if (this.required)
        return this.touchedPhone && (!this.modelValue || !this.phoneValid);

      return (
        this.touchedPhone &&
        this.modelValue &&
        String(this.modelValue).trim() !== '' &&
        !this.phoneValid
      );
    },
    showFieldError() {
      return this.showPhoneError || this.errorOutline;
    },
    activeCountry() {
      return this.country || this.phoneCountry || this.defaultCountry || '';
    },
    
    guessCountryResolver() {
      const explicitCountry = this.country || this.defaultCountry;
      if (!explicitCountry) return undefined;
      return () => explicitCountry;
    }
  },
  methods: {
    onCountryUpdate(value) {
      this.phoneCountry = value || '';
      this.$emit('update:country', value || '');
    },
    onModelValue(value) {
      const v = value != null ? String(value).trim() : '';
      this.$emit('update:modelValue', v);
      this.$emit('input', v);
      const empty = !v;
      if (empty && !this.required) {
        if (!this.phoneValid) {
          this.phoneValid = true;
          this.$emit('validate', { valid: true, number: '' });
        }
      }
    },
    onPhoneObject(phoneObject) {
      const empty = !(this.modelValue && String(this.modelValue).trim());
      const valid = empty
        ? !this.required
        : !!(phoneObject && phoneObject.valid === true);
      const number = (this.modelValue || '').trim();
      if (this.phoneValid !== valid) {
        this.phoneValid = valid;
        this.$emit('validate', { valid, number });
      }
    }
  }
};
</script>

<style lang="scss">
.phone-input-wrapper {
  width: 100%;
}

.phone-input-wrapper .v-messages {
  min-height: 0;
  padding: 0;
}

.phone-input-wrapper .v-messages__wrapper {
  padding: 0;
}

.phone-input-wrapper .v-messages__message {
  font-size: 12px;
  letter-spacing: 0;
  line-height: 1.2;
}

.phone-input-wrapper .v-messages.text-error .v-messages__message {
  color: rgb(var(--v-theme-error, 255 82 82));
}

.phone-input-wrapper .v-input__details {
    align-items: flex-start !important;   
    padding-top: 4px !important;
}

.phone-input :deep(.v-field__label) {
  transform: none !important;
  font-size: inherit !important;
  top: 50% !important;
  opacity: 1 !important;
}
</style>
